import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { FaPlus, FaEdit, FaTrash, FaTimes, FaSearch } from 'react-icons/fa';
import {
  fetchCareerCoaches,
  createCareerCoach,
  updateCareerCoach,
  deleteCareerCoach
} from '../../utils/careerCoachesApi';
import { showSuccess, showError } from '../../components/CustomAlert';
import CustomConfirm from '../../components/CustomConfirm';
import { hasPermission } from '../../utils/permissions';

const Container = styled.div`
  padding: 24px 20px;
  background: #f8f9fa;
  min-height: 100vh;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
`;

const Title = styled.h1`
  color: #2c3e50;
  margin: 0;
  font-size: 1.8rem;
  font-weight: 600;
`;

const AddButton = styled.button`
  background: rgb(51, 66, 147);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: rgb(33, 41, 90);
  }
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const SearchInputWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 220px;
  max-width: 400px;
`;

const SearchIcon = styled(FaSearch)`
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  font-size: 0.9rem;
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 14px 12px 40px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.95rem;
  box-sizing: border-box;
  background: white;

  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);
    box-shadow: 0 0 0 3px rgba(51, 66, 147, 0.12);
  }
`;

const SearchSummary = styled.span`
  color: #6c757d;
  font-size: 0.9rem;
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
  width: 100%;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
`;

const Th = styled.th`
  padding: 16px 20px;
  text-align: left;
  font-weight: 600;
  color: #2c3e50;
  border-bottom: 2px solid #e9ecef;
  font-size: 0.95rem;
  &:nth-child(1) { width: 22%; }
  &:nth-child(2) { width: 20%; }
  &:nth-child(3) { width: 14%; }
  &:nth-child(4) { width: 20%; }
  &:nth-child(5) { width: 10%; }
  &:last-child {
    width: 14%;
    text-align: right;
  }
`;

const Td = styled.td`
  padding: 16px 20px;
  color: #495057;
  font-size: 0.9rem;
  border-bottom: 1px solid #e9ecef;
  word-wrap: break-word;
  overflow-wrap: break-word;
  &:last-child {
    text-align: right;
  }
`;

const Tr = styled.tr`
  &:hover {
    background: #f8f9fa;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const EditButton = styled.button`
  background: rgb(51, 66, 147);
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
`;

const DeleteButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  background: ${(p) => (p.active ? '#d4edda' : '#f8d7da')};
  color: ${(p) => (p.active ? '#155724' : '#721c24')};
`;

const LoadingContainer = styled.div`
  padding: 48px;
  text-align: center;
  color: #6c757d;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
`;

const ModalHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  color: #2c3e50;
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.95rem;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);
  }
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 0.95rem;
  color: #495057;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px 24px;
`;

const SaveButton = styled.button`
  background: rgb(51, 66, 147);
  color: white;
  border: none;
  padding: 10px 22px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  background: #6c757d;
  color: white;
  border: none;
  padding: 10px 22px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
`;

const CloseIconButton = styled.button`
  background: none;
  border: none;
  font-size: 1.2rem;
  color: #6c757d;
  cursor: pointer;
`;

const emptyForm = {
  name: '',
  email: '',
  phone: '',
  organization: '',
  isActive: true
};

const CareerCoaches = () => {
  const { user } = useSelector((state) => state.auth);
  const canCreate = hasPermission(user, 'staff_create');
  const canUpdate = hasPermission(user, 'staff_update');
  const canDelete = hasPermission(user, 'staff_delete');

  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, coach: null });
  const [searchTerm, setSearchTerm] = useState('');

  const filteredCoaches = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return coaches;
    return coaches.filter((coach) => coach.name?.toLowerCase().includes(q));
  }, [coaches, searchTerm]);

  const loadCoaches = async () => {
    try {
      setLoading(true);
      const list = await fetchCareerCoaches();
      setCoaches(list);
    } catch (err) {
      showError(err.message || 'Failed to load career coaches');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCoaches();
  }, []);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (coach) => {
    setEditing(coach);
    setForm({
      name: coach.name || '',
      email: coach.email || '',
      phone: coach.phone || '',
      organization: coach.organization || '',
      isActive: coach.isActive !== false
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
    setForm(emptyForm);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      showError('Name is required');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        organization: form.organization.trim(),
        isActive: form.isActive
      };
      if (editing) {
        await updateCareerCoach(editing.id, payload);
        showSuccess('Career coach updated');
      } else {
        await createCareerCoach(payload);
        showSuccess('Career coach created');
      }
      closeModal();
      loadCoaches();
    } catch (err) {
      showError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.coach) return;
    try {
      await deleteCareerCoach(deleteConfirm.coach.id);
      showSuccess('Career coach deleted');
      setDeleteConfirm({ show: false, coach: null });
      loadCoaches();
    } catch (err) {
      showError(err.message || 'Delete failed');
    }
  };

  return (
    <Container>
      <Header>
        <Title>Career Coaches</Title>
        {canCreate && (
          <AddButton type="button" onClick={openAdd}>
            <FaPlus /> Add Career Coach
          </AddButton>
        )}
      </Header>

      {!loading && coaches.length > 0 && (
        <SearchBar>
          <SearchInputWrap>
            <SearchIcon />
            <SearchInput
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name..."
              aria-label="Search career coaches by name"
            />
          </SearchInputWrap>
          {searchTerm.trim() && (
            <SearchSummary>
              Showing {filteredCoaches.length} of {coaches.length}
            </SearchSummary>
          )}
        </SearchBar>
      )}

      <TableContainer>
        {loading ? (
          <LoadingContainer>Loading career coaches...</LoadingContainer>
        ) : coaches.length === 0 ? (
          <LoadingContainer>No career coaches yet. Add one to get started.</LoadingContainer>
        ) : filteredCoaches.length === 0 ? (
          <LoadingContainer>No career coaches match &quot;{searchTerm.trim()}&quot;.</LoadingContainer>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Phone</Th>
                <Th>Organization</Th>
                <Th>Status</Th>
                <Th style={{ textAlign: 'right' }}>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filteredCoaches.map((coach) => (
                <Tr key={coach.id}>
                  <Td>{coach.name}</Td>
                  <Td>{coach.email || '—'}</Td>
                  <Td>{coach.phone || '—'}</Td>
                  <Td>{coach.organization || '—'}</Td>
                  <Td>
                    <StatusBadge active={coach.isActive}>
                      {coach.isActive ? 'Active' : 'Inactive'}
                    </StatusBadge>
                  </Td>
                  <Td>
                    <ActionButtons>
                      {canUpdate && (
                        <EditButton type="button" onClick={() => openEdit(coach)}>
                          <FaEdit /> Edit
                        </EditButton>
                      )}
                      {canDelete && (
                        <DeleteButton
                          type="button"
                          onClick={() => setDeleteConfirm({ show: true, coach })}
                        >
                          <FaTrash /> Delete
                        </DeleteButton>
                      )}
                    </ActionButtons>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </TableContainer>

      {showModal && (
        <ModalOverlay onClick={closeModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{editing ? 'Edit Career Coach' : 'Add Career Coach'}</ModalTitle>
              <CloseIconButton type="button" onClick={closeModal}>
                <FaTimes />
              </CloseIconButton>
            </ModalHeader>
            <ModalBody>
              <FormGroup>
                <Label>Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Career coach full name"
                />
              </FormGroup>
              <FormGroup>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="email@example.com"
                />
              </FormGroup>
              <FormGroup>
                <Label>Phone</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  placeholder="(555) 555-5555"
                />
              </FormGroup>
              <FormGroup>
                <Label>Organization</Label>
                <Input
                  value={form.organization}
                  onChange={(e) => setForm((f) => ({ ...f, organization: e.target.value }))}
                  placeholder="WIA provider or agency"
                />
              </FormGroup>
              <FormGroup>
                <CheckboxRow>
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                  />
                  Active (available for student assignment)
                </CheckboxRow>
              </FormGroup>
            </ModalBody>
            <Footer>
              <CancelButton type="button" onClick={closeModal}>
                Cancel
              </CancelButton>
              <SaveButton type="button" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
              </SaveButton>
            </Footer>
          </ModalContent>
        </ModalOverlay>
      )}

      <CustomConfirm
        isOpen={deleteConfirm.show}
        onClose={() => setDeleteConfirm({ show: false, coach: null })}
        onConfirm={confirmDelete}
        title="Delete career coach"
        message={`Delete "${deleteConfirm.coach?.name || 'this coach'}"? This cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </Container>
  );
};

export default CareerCoaches;
